import { api } from './campaign/server.ts';

/**
 * Wave notifications (Web Push): the service worker shows them; this asks for permission (only on an
 * explicit tap), subscribes this browser and tells the server.
 */
export type PushState = 'unsupported' | 'install' | 'denied' | 'on' | 'off';

const supported = (): boolean => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

/** iPhone/iPad only push from a web app added to the home screen. */
const needsInstall = (): boolean => {
  const ios = /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const standalone = window.matchMedia('(display-mode: standalone)').matches || (navigator as unknown as { standalone?: boolean }).standalone === true;
  return ios && !standalone;
};

let reg: Promise<ServiceWorkerRegistration | null> | null = null;

/** Registers the service worker once (harmless if notifications are never turned on). */
export const registerWorker = (): Promise<ServiceWorkerRegistration | null> => {
  if (!('serviceWorker' in navigator)) return Promise.resolve(null);
  reg ??= navigator.serviceWorker.register('/sw.js').catch(() => null);
  return reg;
};

export const pushState = async (): Promise<PushState> => {
  if (needsInstall()) return 'install';
  if (!supported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  const r = await registerWorker();
  const sub = await r?.pushManager.getSubscription();
  return sub && Notification.permission === 'granted' ? 'on' : 'off';
};

const keyBytes = (b64: string): Uint8Array => {
  const bin = atob(b64.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (b64.length % 4)) % 4));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
};

/** Turns notifications on: permission, subscription, and the server told. Returns the new state. */
export const enablePush = async (): Promise<PushState> => {
  const now = await pushState();
  if (now === 'install' || now === 'unsupported' || now === 'denied') return now;
  if ((await Notification.requestPermission()) !== 'granted') return 'denied';
  const r = await registerWorker();
  const { key } = await api<{ key: string | null }>('/api/push/key');
  if (!r || !key) return 'unsupported';
  const sub = (await r.pushManager.getSubscription()) ?? (await r.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key) as BufferSource }));
  await api('/api/push', { body: sub.toJSON() });
  return 'on';
};

export const disablePush = async (): Promise<PushState> => {
  const r = await registerWorker();
  const sub = await r?.pushManager.getSubscription();
  if (sub) {
    await api('/api/push/remove', { body: { endpoint: sub.endpoint } }).catch(() => undefined);
    await sub.unsubscribe().catch(() => false);
  }
  return 'off';
};
